import * as React from 'react';
import { FluentProviderProps } from '@fluentui/react-provider';
import { useThemeStyleTag } from '@fluentui/react-provider/lib/components/FluentProvider/useThemeStyleTag';
import {
    useTheme,
    ProviderContext,
    TooltipContext,
    ThemeContext,
    ThemeClassNameContext,
} from '@fluentui/react-shared-contexts';
import { mergeThemes } from '@fluentui/react-theme';
import { useConst } from '@fluentui/react-utilities';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import { webLightTheme, webDarkTheme } from '@fluentui/react-components';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { observer } from 'mobx-react-lite';

// The <FluentProvider> adds an unwanted div to host the CSS variables.
// Until Fluent provides an official way to separate the two concerns
// (hosting CSS Variable values and wrapping React contexts), implementing
// a workaround.
export const FluentContext = observer(function FluentContext({
    theme = getIsDarkTheme() ? webDarkTheme : webLightTheme,
    dir = isCurrentCultureRightToLeft() ? 'rtl' : 'ltr',
    targetDocument = window.document,
    children,
}: Pick<FluentProviderProps, 'theme' | 'dir' | 'targetDocument' | 'children'>) {
    const mergedTheme = mergeThemes(useTheme(), theme);
    const themeClassName = useThemeStyleTag({
        theme: mergedTheme,
        targetDocument,
    });

    const provider = React.useMemo(() => ({ dir, targetDocument }), [dir, targetDocument]);
    // "Tooltip" component mutates an object in this context, instance should be stable
    const tooltip = useConst({});
    return (
        <ProviderContext.Provider value={provider}>
            <ThemeContext.Provider value={mergedTheme}>
                <ThemeClassNameContext.Provider value={themeClassName}>
                    <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>
                </ThemeClassNameContext.Provider>
            </ThemeContext.Provider>
        </ProviderContext.Provider>
    );
});
